import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { useAtom } from 'jotai'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSignUp } from '@/gen/publicApi'

import { Button } from '@/components/button'
import { Form } from '@/components/form'
import FormInput from '@/components/form/formInput'
import { useToast } from '@/components/toast/useToast'

import { authTokenAtom } from '@/state/authToken'

import { mapProblemDetailErrors, passwordSchema } from '@/utils/validation.ts'

import ClientCodeInput from './clientCodeInput'

const signUpformSchema = z.intersection(
    passwordSchema,
    z.object({
        name: z.string(),
        clientName: z.string(),
        clientCode: z.string().min(4, 'Minimum 4 characters').max(8, 'Maximum 8 characters'),
        email: z.string().email(),
    }),
)

type SignUpFormSchema = z.infer<typeof signUpformSchema>

const SignUpForm = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setAuthToken] = useAtom(authTokenAtom)
    const [clientCodeValid, setClientCodeValid] = useState(false)
    const navigate = useNavigate()
    const { redirect } = useSearch({
        from: '/signup/',
    })
    const { toast } = useToast()
    const signupMutator = useSignUp()

    const form = useForm<SignUpFormSchema>({
        resolver: zodResolver(signUpformSchema),
        mode: 'onBlur',
    })

    const onSubmit = (values: SignUpFormSchema) => {
        signupMutator.mutate(values, {
            onSuccess: data => {
                setAuthToken(data.token)
                navigate({
                    to: '/$client_code',
                    params: {
                        client_code: values.clientCode,
                    },
                    search: {
                        welcome: true,
                    },
                })
            },
            // @ts-expect-error - Kubb generated code is not typed correctly
            onError: (error: AxiosError) => {
                mapProblemDetailErrors(error, form.formState.errors)
                toast({
                    variant: 'destructive',
                    title: 'Sign up failed!',
                    description: 'Sign up failed, please try again',
                })
            },
        })
    }

    const onClientCodeBlur = async () => {
        // This is here to make sure we don't remove the error message if the
        // client code is a valid length but already exists in the DB
        const result = await form.trigger('clientCode')
        if (!clientCodeValid && result) {
            form.setError('clientCode', { message: 'Company code unavailable' })
        } else if (result) {
            form.clearErrors('clientCode')
        }
    }

    const onClientCodeValid = (valid: boolean) => {
        setClientCodeValid(valid)
        if (!valid) {
            form.setError('clientCode', {
                message: 'Company code unavailable',
            })
        }
    }
    const isPending = false

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="pt-4 space-y-4">
                <FormInput control={form.control} name="name" label="Name" placeholder="John Doe" autoComplete="name" />
                <FormInput control={form.control} name="email" label="Email" placeholder="you@example.com" />
                <FormInput control={form.control} name="password" type="password" label="Password" />
                <FormInput control={form.control} name="passwordConfirm" type="password" label="Confirm Password" />
                <FormInput control={form.control} name="clientName" label="Company name" placeholder="Acme" />
                <ClientCodeInput onBlur={onClientCodeBlur} form={form} onChange={onClientCodeValid} />

                <div className="flex items-center justify-between">
                    <Button type="submit" disabled={!form.formState.isValid || isPending || !clientCodeValid}>
                        {isPending ? 'Signing up' : 'Sign up'}
                    </Button>
                    <Link to={`/login`} search={{ redirect }} className="text-sm">
                        Or login to an existing account
                    </Link>
                </div>
            </form>
        </Form>
    )
}

export default SignUpForm
